import React from 'react'
import { css, cx } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import { PureQueryOptions } from 'apollo-client'
import { Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import { CellRenderer, CellProps } from '../../../legacy-design-system/grid'
import { LoadingTextMask } from '../../../legacy-design-system/loading-masks'
import { PendingStatus } from '../shared'
import {
  Status,
  defaultStatusRegistry,
  UpdateStatusManager
} from '../../vulnerability-status'
import {
  SinkGroupStatus,
  SinkGroupStatusName
} from '../../vulnerability-status/sink-group-status'
import { useOrganizationFeature } from '../../organization-feature'
import { Role, RoleSwitch } from '../../roles'
import { EditableTextDropdown } from '../../../legacy-design-system/editable-text'
import { I18nConsumer } from '../../i18n-provider'
import { readOnlyGridCss } from '../../../legacy-design-system/text'
import { PendingStatusIndicator } from '../../pending-status-indicator'
import { BugTrackerTooltip, BugTracker } from '../../bug-tracker'

interface GetRendererProps {
  organizationID: string
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>
}

export interface Vulnerability {
  id: string
  status: string
  substatus?: string
  reportedToBugTracker: boolean
  bugTrackers: BugTracker[]
  application: {
    role: Role
  }
  visible: boolean
  pendingStatus?: PendingStatus
}

interface SinkGroup {
  status: SinkGroupStatusName
}

export interface RowData {
  sinkGroup?: SinkGroup
  vulnerability?: Vulnerability
}

const containerCss = css`
  display: flex;
`

const iconCss = css`
  align-self: center;
  transform: translateY(-1px);
  margin-top: 2px;
`

const substatusCss = css`
  display: flex;
  font-size: 12px;
  display: flex;
  color: #bfbfbf;
`

const substatusCssStyle = cx(
  substatusCss,
  css`
    position: absolute;
    bottom: 17px;
    margin-left: 9px;
  `
)

export function getStatusCellRenderer({
  organizationID,
  refetchQueries,
  refresh
}: GetRendererProps): CellRenderer<RowData> {
  return ({ rowData, getCellContentProps }) => {
    if (rowData.vulnerability) {
      return renderStatusCell({
        organizationID,
        refetchQueries,
        refresh,
        rowData: rowData.vulnerability,
        getCellContentProps
      })
    } else if (rowData.sinkGroup) {
      return renderSinkGroupStatusCell({
        getCellContentProps,
        rowData: rowData.sinkGroup
      })
    }
    throw new Error(
      `Invalid status row. Row data should contain a vulnerability or sink group: ${rowData}`
    )
  }
}

function renderSinkGroupStatusCell({
  getCellContentProps,
  rowData: { status }
}: CellProps<SinkGroup>) {
  return (
    <div {...getCellContentProps({ className: css(`cursor: default;`) })}>
      <FormattedMessage id={SinkGroupStatus.fromName(status).i18nID}>
        {(text) => {
          return <span className={readonlyTextCss}>{text}</span>
        }}
      </FormattedMessage>
    </div>
  )
}

function renderStatusCell({
  organizationID,
  refetchQueries,
  refresh,
  rowData: {
    id,
    status,
    substatus,
    reportedToBugTracker,
    bugTrackers,
    application: { role },
    visible,
    pendingStatus
  },
  getCellContentProps
}: GetRendererProps & CellProps<Vulnerability>) {
  const paddingSubstatus = substatus ? 10 : 0
  const paddingPeding = pendingStatus ? 10 : 0
  const visibleStatus = pendingStatus ? pendingStatus.status : status
  return (
    <div {...getCellContentProps({})}>
      <span className={containerCss} data-e2e={`status-dropdown-${status}`}>
        {visible ? (
          <div
            className={css`
              height: 100%;
            `}
          >
            <RoleSwitch
              role={role}
              requiredRole="EDIT"
              renderAllowed={() => {
                return (
                  <div
                    className={css`
                      flex-direction: column;
                      align-items: center;
                    `}
                  >
                    <div
                      className={css`
                        position: absolute;
                        top: calc(
                          ${50 - (paddingSubstatus + paddingPeding)}% - 18px
                        );
                        display: flex;
                        height: 34px;
                      `}
                    >
                      <StatusInline
                        status={visibleStatus}
                        organizationID={organizationID}
                        id={id}
                        refetchQueries={refetchQueries}
                        refresh={refresh}
                        role={role}
                      />
                      {reportedToBugTracker && (
                        <BugTrackerTooltip
                          organizationID={organizationID}
                          vulnerabilityID={id}
                          iconClassName={iconCss}
                          tooltipClassName={css`
                            min-width: 250px;
                          `}
                          bugTrackers={bugTrackers}
                        />
                      )}
                    </div>
                    <Substatus
                      substatus={substatus}
                      cssSubstatusStyle={substatusCssStyle}
                    />
                    {pendingStatus && (
                      <div
                        className={css`
                          position: absolute;
                          bottom: 15px;
                          margin-left: 5px;
                          display: flex;
                          height: 15px;
                        `}
                      >
                        <PendingStatusIndicator
                          created={pendingStatus.created}
                        />
                      </div>
                    )}
                  </div>
                )
              }}
              renderDenied={() => {
                return (
                  <ViewOnlyContent
                    status={visibleStatus}
                    substatus={substatus}
                    organizationID={organizationID}
                    vulnerabilityID={id}
                    reportedToBugTracker={reportedToBugTracker}
                    bugTrackers={bugTrackers}
                    pendingStatus={pendingStatus}
                  />
                )
              }}
            />
          </div>
        ) : (
          <ReadOnlyStatus status={status} substatus={substatus} />
        )}
      </span>
    </div>
  )
}

const viewOnlyContainerCss = css`
  padding: 0 10px 0;
  padding-left: 8px;
  padding-right: 8px;
  display: flex;
`

const viewOnlyContentCss = css`
  margin-left: 5px;
`

const viewOnlyIconCss = css`
  align-self: center;
  transform: translateY(-1px);
  margin-left: 8px;
`
const viewOnlySubstatusCss = cx(
  substatusCss,
  css`
    margin-top: 5px;
    margin-left: 8px;
  `
)

function ViewOnlyContent({
  status,
  substatus,
  reportedToBugTracker,
  bugTrackers,
  pendingStatus,
  organizationID,
  vulnerabilityID
}: {
  status: string
  organizationID: string
  vulnerabilityID: string
  reportedToBugTracker: boolean
  bugTrackers: BugTracker[]
  pendingStatus?: PendingStatus
  substatus?: string
}) {
  return (
    <div>
      <div className={viewOnlyContainerCss}>
        <FormattedMessage id={defaultStatusRegistry.fromName(status).i18nID}>
          {(text) => <span>{text}</span>}
        </FormattedMessage>
        {reportedToBugTracker && (
          <BugTrackerTooltip
            organizationID={organizationID}
            vulnerabilityID={vulnerabilityID}
            iconClassName={viewOnlyIconCss}
            tooltipClassName={css`
              min-width: 250px;
            `}
            bugTrackers={bugTrackers}
          />
        )}
      </div>
      <Substatus
        substatus={substatus}
        cssSubstatusStyle={viewOnlySubstatusCss}
      />
      <div className={viewOnlyContentCss}>
        {pendingStatus && (
          <PendingStatusIndicator created={pendingStatus.created} />
        )}
      </div>
    </div>
  )
}

interface StatusInlineProps {
  status: string
  organizationID: string
  id: string
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>
  role: Role
}

interface MutationResponse {
  updateVulnerabilitiesStatus: {
    _placeholder: boolean
    __typename: string
  }
  __typename: string
}

interface MutationVars {
  organizationID: string
  ids: string[]
  status: string
  substatus?: string
  reasoning?: string
}

function StatusInline({
  status,
  organizationID,
  id,
  refetchQueries,
  refresh,
  role
}: StatusInlineProps) {
  const { enabled } = useOrganizationFeature(
    organizationID,
    'approval-workflow'
  )

  return (
    <Mutation<MutationResponse, MutationVars>
      mutation={gql`
        mutation UpdateVulnerabilityStatus(
          $organizationID: ID!
          $ids: [ID!]!
          $status: VulnerabilityStatus!
          $substatus: String
          $reasoning: String
        ) {
          updateVulnerabilitiesStatus(
            input: {
              organizationID: $organizationID
              ids: $ids
              status: $status
              substatus: $substatus
              reasoning: $reasoning
            }
          ) {
            _placeholder
          }
        }
      `}
    >
      {(updateStatus) => {
        return (
          <UpdateStatusManager
            organizationID={organizationID}
            traceIDs={[id]}
            isStatusApprovalEnabled={Boolean(enabled)}
            updateStatus={({ status, substatus, reasoning }) => {
              return updateStatus({
                variables: {
                  organizationID,
                  ids: [id],
                  status: status.name,
                  substatus: substatus && substatus.name,
                  reasoning
                },
                refetchQueries,
                optimisticResponse: {
                  updateVulnerabilitiesStatus: {
                    _placeholder: true,
                    __typename: 'GenericMutationResponse'
                  },
                  __typename: 'Mutation'
                }
              }).then(refresh)
            }}
          >
            {({
              selectedStatus = defaultStatusRegistry.fromName(status),
              onSelectStatus
            }) => {
              return (
                <I18nConsumer>
                  {({ intl }) => {
                    return (
                      <EditableTextDropdown<Status>
                        e2eTestId="vuln-status-dropdown"
                        menuE2eTestId="vuln-status-options"
                        styleOverrides={{
                          selectionContainerCss: css`
                            padding-left: 8px;
                            padding-right: 8px;
                            &:not(:hover) {
                              background-image: none;
                              border-color: transparent;
                            }
                          `
                        }}
                        items={defaultStatusRegistry.manual.filter((status) =>
                          status.isAllowedByRole(role)
                        )}
                        selectedItem={selectedStatus}
                        itemToString={(status) => {
                          return intl.formatMessage({
                            id: status.i18nID
                          })
                        }}
                        onChange={onSelectStatus}
                      />
                    )
                  }}
                </I18nConsumer>
              )
            }}
          </UpdateStatusManager>
        )
      }}
    </Mutation>
  )
}

export const readonlyTextCss = cx(
  readOnlyGridCss,
  css`
    padding: 8px;
  `
)

const readonlySubstatusCss = cx(
  substatusCss,
  css`
    margin-left: 9px;
    margin-top: 4px;
  `
)

function ReadOnlyStatus({ status, substatus }) {
  return (
    <span>
      <FormattedMessage id={defaultStatusRegistry.fromName(status).i18nID}>
        {(text) => {
          return <span className={readonlyTextCss}>{text}</span>
        }}
      </FormattedMessage>
      <Substatus
        substatus={substatus}
        cssSubstatusStyle={readonlySubstatusCss}
      />
    </span>
  )
}

function Substatus({ substatus, cssSubstatusStyle }) {
  return (
    <span>
      {substatus && <div className={cssSubstatusStyle}>{substatus}</div>}
    </span>
  )
}

const loadingCellCss = css`
  padding: 8px;
  width: 100%;
`

export function loadingStatusCellRenderer() {
  return (
    <span className={loadingCellCss}>
      <LoadingTextMask min={6} max={7} />
    </span>
  )
}
